import React, { useState, useEffect } from 'react';
import { logService } from '../../services/logService';
import { printReport } from '../../utils/printUtils'; // 공용 인쇄 리포트 유틸 연동

// 기록 시각 표시용. 서버 시간(createdAt)을 우선 쓰고, 없으면 ISO(at), 그것도 없으면 옛 문자열을 쓴다.
const pad2 = (n) => String(n).padStart(2, '0');
const formatWhen = (log) => {
  const toText = (d) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
  try {
    if (log.createdAt && typeof log.createdAt.toDate === 'function') return toText(log.createdAt.toDate());
    if (log.at) {
      const d = new Date(log.at);
      if (!isNaN(d.getTime())) return toText(d);
    }
  } catch (e) { /* 형식이 이상하면 아래 기본값 */ }
  return log.timestamp || '-';
};

const SmsTemplate = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // 변경된 핵심 부문 및 행위 검색 상태
  const [filterModule, setFilterModule] = useState('전체');
  const [filterAction, setFilterAction] = useState('전체');
  const [searchOperator, setSearchOperator] = useState('전체'); // '전체' 선택 기본값 세팅

  // 🚀 실제 기록(E2B_SYSTEM_LOGS)을 최신순으로 불러온다. 각 업무 서비스가 저장 시점에 남긴 이력이다.
  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const raw = await logService.getLogs(200);
      const rows = (raw || []).map(l => ({
        id: l.id,
        timestamp: formatWhen(l),
        module: l.module || '기타',
        action: l.action || 'UPDATE',
        // 작업자에는 로그인한 사람의 '등록된 이름'이 들어온다(옛 기록은 user 필드에 있었다).
        operator: l.operator || l.user || '알수없음',
        target: l.target || '-',
        details: l.details || '-'
      }));
      setLogs(rows);
    } catch (error) {
      console.error("행위 이력 로그 로드 실패", error);
      setLogs([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  // 부문(Module) 및 행위(Action), 작업자 필터 결합 로직
  const filteredLogs = logs.filter(log => {
    const matchModule = filterModule === '전체' || log.module === filterModule;
    const matchAction = filterAction === '전체' || log.action === filterAction;
    const matchOperator = searchOperator === '전체' || log.operator === searchOperator;
    return matchModule && matchAction && matchOperator;
  });

  // 실제 기록에 등장한 작업자만 골라 필터 목록을 만든다(하드코딩 목록과 어긋나지 않게).
  const operatorOptions = Array.from(new Set(logs.map(l => l.operator).filter(Boolean))).sort();

  const handleReset = () => {
    setFilterModule('전체');
    setFilterAction('전체');
    setSearchOperator('전체');
  };

  const handlePrintReport = () => {
    const title = "시스템 데이터 전 부문 행위 이력 감사 리포트 (Audit Log)";
    const headers = ["일시 (Timestamp)", "대상 부문", "행위 유형", "수행 작업자", "관리 타겟 명칭", "상세 행위 스토리 기록"];
    const rows = filteredLogs.map(l => [
      l.timestamp,
      l.module,
      l.action,
      l.operator,
      l.target,
      l.details
    ]);
    const metaInfo = {
      "보안 목적": "인위적 데이터 위변조 및 해킹 훼손 방지용 상시 모니터링 증빙",
      "출력 조건": `부문: ${filterModule} | 행위: ${filterAction} | 작업자: ${searchOperator}`
    };
    printReport(title, headers, rows, metaInfo);
  };

  const getActionBadgeStyle = (action) => {
    const base = { padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', display: 'inline-block' };
    if (action === 'CREATE') return { ...base, background: '#e0f2fe', color: '#0369a1' }; // 생성 (Blue)
    if (action === 'UPDATE') return { ...base, background: '#fef3c7', color: '#b45309' }; // 수정 (Orange)
    if (action === 'DELETE') return { ...base, background: '#fee2e2', color: '#b91c1c' }; // 삭제 (Red)
    if (action === 'LOGIN') return { ...base, background: '#dcfce7', color: '#15803d' };  // 로그인 (Green)
    return { ...base, background: '#f1f5f9', color: '#475569' };
  };

  const styles = {
    wrapper: { padding: '40px', background: '#f8fafc', minHeight: '100vh', boxSizing: 'border-box' },
    headerArea: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
    title: { fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' },
    subTitle: { fontSize: '13px', color: '#64748b', marginTop: '5px', marginBottom: '25px' },
    printBtn: { padding: '10px 20px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' },
    
    // 필터 영역 스타일
    // 버튼 칸은 내용만큼만(auto) 차지하게 해서 글자가 세로로 찌그러지지 않도록 한다.
    filterCard: { background: 'white', padding: '20px 25px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0', display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr auto', gap: '15px', alignItems: 'flex-end', marginBottom: '20px' },
    filterLabel: { fontSize: '12px', fontWeight: 'bold', color: '#475569', marginBottom: '6px', display: 'block' },
    select: { width: '100%', padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', background: 'white', outline: 'none', fontSize: '14px', cursor: 'pointer' },
    input: { width: '100%', padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', outline: 'none', fontSize: '14px', boxSizing: 'border-box' },
    btnSearch: { padding: '11px 24px', background: '#1e293b', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap' },
    btnReset: { padding: '11px 16px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap' },
    
    // 감사 테이블 뷰 스타일
    tableCard: { background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
    th: { padding: '16px 20px', background: '#1e293b', color: '#f8fafc', fontSize: '13px', fontWeight: '700' },
    td: { padding: '16px 20px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', color: '#334155', verticalAlign: 'middle' },
    row: { transition: 'background 0.2s', '&:hover': { background: '#f8fafc' } }
  };

  return (
    <div style={styles.wrapper}>
      {/* 관리지표 상단 타이틀 */}
      <div style={styles.headerArea}>
        <h2 style={styles.title}>📝 시스템 데이터 전 부문 행위 이력 감사 (Audit Log)</h2>
        <button style={styles.printBtn} onClick={handlePrintReport}>
          🖨️ 감사 리포트 인쇄 및 PDF 출력
        </button>
      </div>
      <div style={styles.subTitle}>
        AS관리, 고객관리, 차량관리, 리콜관리, 부품관리에서 발생하는 모든 생성(CREATE), 수정(UPDATE), 삭제(DELETE) 스토리를 타임라인별로 추적하여, 악의적인 조작 및 해킹 훼손을 차단합니다.
      </div>

      {/* 부문별 행위 이력 실시간 소팅 필터 패널 */}
      <div style={styles.filterCard}>
        <div>
          <label style={styles.filterLabel}>대상 관리 부문</label>
          <select style={styles.select} value={filterModule} onChange={e => setFilterModule(e.target.value)}>
            <option value="전체">전체 부문 보기</option>
            <option value="AS관리">🛠️ AS관리</option>
            <option value="고객관리">👤 고객관리</option>
            <option value="차량관리">🚘 차량관리</option>
            <option value="리콜관리">⚠️ 리콜관리</option>
            <option value="부품관리">⚙️ 부품관리</option>
            <option value="시스템">🔐 시스템(로그인)</option>
          </select>
        </div>
        <div>
          <label style={styles.filterLabel}>행위 구분 (Action)</label>
          <select style={styles.select} value={filterAction} onChange={e => setFilterAction(e.target.value)}>
            <option value="전체">전체 행위</option>
            <option value="CREATE">CREATE (생성)</option>
            <option value="UPDATE">UPDATE (수정)</option>
            <option value="DELETE">DELETE (삭제)</option>
            <option value="LOGIN">LOGIN (로그인)</option>
          </select>
        </div>
        <div>
          <label style={styles.filterLabel}>행위자 검색 (Operator)</label>
          <select style={styles.select} value={searchOperator} onChange={e => setSearchOperator(e.target.value)}>
            <option value="전체">전체 행위자 보기</option>
            {operatorOptions.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button style={styles.btnSearch} onClick={fetchAuditLogs}>새로고침 조회</button>
          <button style={styles.btnReset} onClick={handleReset}>필터 비우기</button>
        </div>
      </div>

      {/* 로그 탐지 집계 인디케이터 */}
      <div style={{ fontSize: '13px', color: '#475569', fontWeight: '600', marginBottom: '15px', paddingLeft: '5px' }}>
        실시간 보안 모니터링 추적 건수: <span style={{ color: '#6366f1', fontWeight: 'bold' }}>{filteredLogs.length}건</span>
      </div>

      {/* 감사 로그 타임라인 매트릭스 보드 */}
      <div style={styles.tableCard}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: '15%' }}>일시 (Timestamp)</th>
              <th style={{ ...styles.th, width: '12%' }}>대상 부문</th>
              <th style={{ ...styles.th, width: '10%' }}>행위 구분</th>
              <th style={{ ...styles.th, width: '12%' }}>수행 작업자</th>
              <th style={{ ...styles.th, width: '18%' }}>관리 타겟 명칭</th>
              <th style={{ ...styles.th, width: '33%' }}>상세 행위 스토리 기록 (Payload)</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" style={{ ...styles.td, textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                  보안 감사 타임라인 데이터를 동기화하고 있습니다...
                </td>
              </tr>
            ) : filteredLogs.length > 0 ? (
              filteredLogs.map(log => (
                <tr key={log.id} style={styles.row}>
                  <td style={{ ...styles.td, color: '#64748b', fontWeight: '500' }}>{log.timestamp}</td>
                  <td style={{ ...styles.td, fontWeight: '700', color: '#4f46e5' }}>{log.module}</td>
                  <td style={styles.td}>
                    <span style={getActionBadgeStyle(log.action)}>{log.action}</span>
                  </td>
                  <td style={{ ...styles.td, color: '#0f172a', fontWeight: '600' }}>
                    💻 {log.operator}
                  </td>
                  <td style={{ ...styles.td, color: '#334155', fontWeight: 'bold' }}>{log.target}</td>
                  <td style={{ ...styles.td, color: '#1e293b', fontWeight: '500', lineHeight: '1.4' }}>{log.details}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{ ...styles.td, textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                  조건과 일치하는 행위 감사 이력이 없습니다.<br />
                  <span style={{ fontSize: '12px' }}>
                    이력은 <b>기록 기능이 적용된 시점부터</b> 쌓입니다. 고객·차량·AS·부품·리콜을 등록/수정/삭제하면 이곳에 자동으로 남습니다.
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SmsTemplate;